import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import PublicSite from "@/pages/PublicSite";
import "@/App.css";

// Detect which console a subdomain maps to (admin.* / client.*). Null = main marketing site.
export function hostConsole() {
  if (typeof window === "undefined") return null;
  const h = window.location.hostname.toLowerCase();
  if (h.startsWith("admin.")) return "admin";
  if (h.startsWith("client.")) return "client";
  return null;
}

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="h-10 w-10 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" />
    </div>
  );
}

// Root entry: subdomain-aware. Apex/preview shows marketing; admin./client. jump into the app.
function RootEntry() {
  const { user } = useAuth();
  const onConsoleSubdomain = hostConsole() !== null;
  if (!onConsoleSubdomain) return <Landing />;
  if (user === undefined) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  // Role decides the console; access to each is enforced by the route guards.
  return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App font-body">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootEntry />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Navigate to="/login" replace />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/view/:churchId" element={<PublicSite />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
